import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiBarChart2,
  FiBriefcase,
  FiCreditCard,
  FiLogOut,
  FiMenu,
  FiTruck,
  FiUser,
  FiUserCheck,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { setDarkMode } from '../../reducers/darkSlice';
import { useSession } from '../../context/SessionContext';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSelector } from './LanguageSelector';
import { isAdmin } from '../../utils/permissions';
import { AppTooltip } from '../ui/AppTooltip';
import axiosInstance from '../../utils/axiosInstance';

const routeMeta = {
  '/dashboard': 'Dashboard',
  '/transactions': 'Transactions',
  '/customers': 'Customers',
  '/users': 'Users',
  '/suppliers': 'Suppliers',
  '/profile': 'My profile',
};

const getTitleKey = (pathname) => routeMeta[pathname] || 'Dashboard';

const NavItem = ({ to, icon: Icon, label, onClick }) => (
  <AppTooltip label={label} placement='right'>
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-semibold ${
          isActive
            ? 'bg-[var(--brand-soft)] text-[var(--brand-strong)] dark:text-[var(--brand-strong)]'
            : 'text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
        }`
      }
    >
      <Icon className='h-5 w-5 shrink-0' aria-hidden='true' />
      <span>{label}</span>
    </NavLink>
  </AppTooltip>
);

const TopNavItem = ({ to, icon: Icon, label }) => (
  <AppTooltip label={label} placement='bottom'>
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex h-10 w-full min-w-0 items-center justify-center gap-2 rounded-[var(--radius-md)] px-2 text-sm font-semibold 2xl:px-3 ${
          isActive
            ? 'bg-[var(--brand-soft)] text-[var(--brand-strong)]'
            : 'text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
        }`
      }
    >
      <Icon className='h-4 w-4 shrink-0' aria-hidden='true' />
      <span className='hidden min-w-0 truncate 2xl:inline'>{label}</span>
    </NavLink>
  </AppTooltip>
);

export const AppShell = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [, , removeCookie] = useCookies(['invoice_csrf']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user: userData, role, clearSession } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useSelector((state) => state.darkMode.isDarkMode);

  const isLoginPage = location.pathname === '/login'
    || location.pathname === '/two-factor'
    || location.pathname === '/two-factor/tutorial'
    || location.pathname === '/';
  const title = t(getTitleKey(location.pathname));

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.style.backgroundColor = isDarkMode ? '#0b1120' : '#f5f7fb';
    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      isDarkMode ? '#0b1120' : '#f5f7fb',
    );
  }, [isDarkMode]);

  useEffect(() => {
    const storedTheme = window.localStorage.getItem('theme');
    if (!storedTheme && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
      dispatch(setDarkMode(true));
    }
  }, [dispatch]);

  useEffect(() => {
    if (userData?.theme) dispatch(setDarkMode(userData.theme === 'dark'));
  }, [dispatch, userData?.theme]);
  const navigation = useMemo(
    () => [
      { to: '/dashboard', label: t('Dashboard'), icon: FiBarChart2, show: true },
      {
        to: '/transactions?customer_id=&&supplier_id=',
        label: t('Transactions'),
        icon: FiCreditCard,
        show: true,
      },
      { to: '/customers', label: t('Customers'), icon: FiUsers, show: isAdmin(role) },
      { to: '/users', label: t('Users'), icon: FiUserCheck, show: isAdmin(role) },
      { to: '/suppliers', label: t('Suppliers'), icon: FiTruck, show: isAdmin(role) },
      { to: '/profile', label: t('My profile'), icon: FiUser, show: true },
    ],
    [role, t],
  );

  const logout = async () => {
    try {
      await axiosInstance('/user/logout', 'post', {});
    } finally {
      clearSession();
      removeCookie('invoice_csrf', { path: '/' });
      navigate('/login');
    }
  };

  if (isLoginPage) {
    return (
      <div className='min-h-screen bg-[var(--surface-page)]'>
        <ToastContainer position='top-right' theme={isDarkMode ? 'dark' : 'light'} />
        {children}
      </div>
    );
  }

  const sidebar = (
    <aside
      className='flex h-full w-[300px] flex-col border-r border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-4 shadow-sm'
    >
      <div className='mb-6 flex items-center justify-between'>
        <Link to='/dashboard' className='flex items-center gap-3'>
          <span className='flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand)] text-white'>
            <FiBriefcase aria-hidden='true' />
          </span>
          <span>
            <span className='block text-sm font-bold text-[var(--text-primary)]'>
              {t('Billing Management')}
            </span>
            <span className='block text-xs text-[var(--text-muted)]'>{t('Finance workspace')}</span>
          </span>
        </Link>
        <AppTooltip label={t('Close navigation')} placement='right'><button
          type='button'
          className='flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:bg-[var(--surface-panel-muted)]'
          onClick={() => setMobileOpen(false)}
          aria-label={t('Close navigation')}
        >
          <FiX aria-hidden='true' />
        </button></AppTooltip>
      </div>

      <nav className='flex flex-1 flex-col gap-1'>
        {navigation
          .filter((item) => item.show)
          .map((item) => (
            <NavItem
              key={item.to}
              {...item}
              onClick={() => setMobileOpen(false)}
            />
          ))}
      </nav>
    </aside>
  );

  return (
    <div className='min-h-screen bg-[var(--surface-page)] text-[var(--text-primary)]'>
      <ToastContainer position='top-right' theme={isDarkMode ? 'dark' : 'light'} />
      {mobileOpen && (
        <div className='fixed inset-0 z-50 lg:hidden'>
          <button
            type='button'
            className='absolute inset-0 bg-slate-950/50'
            aria-label={t('Close navigation')}
            onClick={() => setMobileOpen(false)}
          />
          <div className='relative h-full w-[300px]'>{sidebar}</div>
        </div>
      )}

      <div>
        <header className='sticky top-0 z-40 border-b border-[var(--border-subtle)] bg-[var(--surface-page)]/92 backdrop-blur'>
          <div className='flex min-h-[72px] items-center justify-between gap-4 px-4 sm:px-6 lg:grid lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:px-8 xl:grid-cols-[260px_minmax(0,1fr)_auto]'>
            <div className='flex min-w-0 items-center gap-3'>
              <AppTooltip label={t('Open navigation')} placement='bottom'><button
                type='button'
                className='inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] text-[var(--text-secondary)] lg:hidden'
                onClick={() => setMobileOpen(true)}
                aria-label={t('Open navigation')}
              >
                <FiMenu aria-hidden='true' />
              </button></AppTooltip>
              {mobileOpen && (
                <button
                  type='button'
                  className='sr-only'
                  onClick={() => setMobileOpen(false)}
                  aria-label={t('Close navigation')}
                >
                  <FiX />
                </button>
              )}
              <div className='min-w-0'>
                <div className='text-xs font-medium text-[var(--text-muted)]'>
                  {t('Home')} / {title}
                </div>
                <h1 className='truncate text-lg font-bold text-[var(--text-primary)]'>{title}</h1>
              </div>
            </div>

            <nav className='mx-auto hidden w-full max-w-[960px] grid-flow-col auto-cols-fr items-center gap-2 lg:grid' aria-label={t('Navigation')}>
              {navigation.filter((item) => item.show).map((item) => (
                <TopNavItem key={item.to} {...item} />
              ))}
            </nav>

            <div className='flex items-center gap-2 lg:justify-self-end'>
              <LanguageSelector
                value={i18n.language}
                onChange={(lng) => i18n.changeLanguage(lng)}
                label={t('Change language')}
              />
              <ThemeToggle label={t('Toggle theme')} persist />
              <div className='hidden items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-2 sm:flex'>
                <div className='flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand-soft)] text-[var(--brand)]'>
                  <FiUser aria-hidden='true' />
                </div>
                <div className='max-w-[180px]'>
                  <div className='truncate text-sm font-semibold text-[var(--text-primary)]'>
                    {userData ? `${userData.firstName || ''} ${userData.lastName || ''}` : t('User')}
                  </div>
                  <div className='truncate text-xs text-[var(--text-muted)]'>
                    {role === 'admin' ? t('Administrator') : t('Customer')}
                  </div>
                </div>
              </div>
              <AppTooltip label={t('Log out')} placement='bottom'><button
                type='button'
                onClick={logout}
                className='inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)]'
                aria-label={t('Log out')}
              >
                <FiLogOut aria-hidden='true' />
              </button></AppTooltip>
            </div>
          </div>
        </header>

        <main className='mx-auto w-full max-w-[1920px] px-4 py-6 sm:px-6 lg:px-8'>{children}</main>
      </div>
    </div>
  );
};
